import React, { useContext } from "react";
import { createBrowserRouter, createRoutesFromElements, Route, RouterProvider } from "react-router-dom";
import { useSelector } from "react-redux";
// import i18n from "i18next";
import NotFoundErrorPage from "../errors/NotFoundErrorPage";
import RouterErrorHandler from "../errors/RouterErrorHandler";
import PrivateLayout from "../app/Layout/PrivateLayout";
import PublicLayout from "../app/Layout/PublicLayout";
import routes, { routesParts } from "./routes";
import Navigate from "../../vendor/router/Navigate";
import RoutesContextProvider from "../../vendor/router/RoutesContextProvider";
import { configSelectors } from "../../redux/config";
import useCreatePublicLazyLoader from "../../hooks/useCreatePublicLazyLoader";
import useCreatePrivateLazyLoader from "../../hooks/useCreatePrivateLazyLoader";
import AnafSpvAccountantFinishPage from "../spv/AnafSpvAccountantFinishPage";
import { I18nContext } from "react-i18next";
import ModalManagerProvider from "./../app/ModalManagerContext";
export default function Router() {
    var locales = useSelector(configSelectors.locales);
    var i18n = useContext(I18nContext).i18n;
    /**
     * These 3 (lazy)loader creator functions ensure that:
     * 1) if there is an error already in the loaders chain, they do not continue
     * 2) if the user is / is not authenticated, they redirect to the proper location (login/dash)
     */ var createPublicLazyLoader = useCreatePublicLazyLoader(i18n);
    var createPrivateLazyLoader = useCreatePrivateLazyLoader(i18n);
    return /*#__PURE__*/ React.createElement(RoutesContextProvider, {
        routes: routes,
        locales: locales
    }, /*#__PURE__*/ React.createElement(RouterProvider, {
        router: createBrowserRouter(createRoutesFromElements(/*#__PURE__*/ React.createElement(Route, {
            ErrorBoundary: RouterErrorHandler
        }, /*#__PURE__*/ React.createElement(Route, {
            element: /*#__PURE__*/ React.createElement(ModalManagerProvider, null)
        }, /*#__PURE__*/ React.createElement(Route, {
            path: ".well-known/change-password",
            element: function() {
                return /*#__PURE__*/ React.createElement(Navigate, {
                    to: routesParts.AUTH_FORGOT_PASSWORD
                });
            }
        }), /*#__PURE__*/ React.createElement(Route, {
            element: /*#__PURE__*/ React.createElement(PrivateLayout, null)
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.DASHBOARD,
            lazy: createPrivateLazyLoader(function() {
                return import("../dashboard/DashboardPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.NOTIFICATIONS_LIST,
            lazy: createPrivateLazyLoader(function() {
                return import("../../features/notifications/NotificationsPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PARTNERS,
            lazy: createPrivateLazyLoader(function() {
                return import("../partners/ListPartnersPage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PARTNERS_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../partners/CreatePartnerPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PARTNERS_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../partners/PartnerPage");
            }),
            id: "partner",
            shouldRevalidate: function(param) {
                var currentParams = param.currentParams, nextParams = param.nextParams;
                /**
                                             * We need to do this because shouldRevalidate will prevent
                                             * our revalidate call from EditPartnerAddress and the
                                             * HQ address won't be synced between Details and Addresses tabs
                                             */ var updatedAddress = currentParams.addressId && !nextParams.addressId;
                return nextParams.id !== currentParams.id || !!updatedAddress;
            }
        }, /*#__PURE__*/ React.createElement(Route, {
            index: true,
            lazy: createPrivateLazyLoader(function() {
                return import("../partners/EditPartnerPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PARTNERS_ADDRESSES,
            lazy: createPrivateLazyLoader(function() {
                return import("../partnersAddresses/ListPartnerAddressesPage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PARTNERS_ADDRESSES_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../partnersAddresses/CreatePartnerAddressPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PARTNERS_ADDRESSES_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../partnersAddresses/EditPartnerAddressPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PARTNERS_CONTACTS,
            lazy: createPrivateLazyLoader(function() {
                return import("../partnersContacts/ListPartnerContactsPage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PARTNERS_CONTACTS_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../partnersContacts/CreatePartnersContactPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PARTNERS_CONTACTS_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../partnersContacts/EditPartnerContactPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PARTNERS_BANK_ACCOUNTS,
            lazy: createPrivateLazyLoader(function() {
                return import("../partnersBankAccounts/ListPartnerBankAccountsPage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PARTNERS_BANK_ACCOUNTS_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../partnersBankAccounts/CreatePartnerBankAccountPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PARTNERS_BANK_ACCOUNTS_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../partnersBankAccounts/EditPartnerBankAccountPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PRODUCTS_CATEGORIES,
            lazy: createPrivateLazyLoader(function() {
                return import("../productsCategories/ListProductsCategoriesPage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PRODUCTS_CATEGORIES_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../productsCategories/CreateProductsCategoryPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PRODUCTS_CATEGORIES_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../productsCategories/EditProductsCategoryPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PRODUCTS,
            lazy: createPrivateLazyLoader(function() {
                return import("../products/ListProductsPage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PRODUCTS_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../products/CreateProductPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PRODUCTS_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../products/ProductPage");
            }),
            id: "products"
        }, /*#__PURE__*/ React.createElement(Route, {
            index: true,
            lazy: createPrivateLazyLoader(function() {
                return import("../products/EditProductPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PRODUCTS_ATTRIBUTES,
            lazy: createPrivateLazyLoader(function() {
                return import("../productsAttributes/ListProductAttributesPage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PRODUCTS_ATTRIBUTES_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../productsAttributes/CreateProductAttributePage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PRODUCTS_ATTRIBUTES_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../productsAttributes/EditProductAttributePage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PRODUCTS_PACKAGING,
            lazy: createPrivateLazyLoader(function() {
                return import("../productsPackaging/ListProductPackagingPage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PRODUCTS_PACKAGING_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../productsPackaging/CreateProductPackagingPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PRODUCTS_PACKAGING_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../productsPackaging/EditProductPackagingPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PRODUCTS_CLIENTS_OFFERS,
            lazy: createPrivateLazyLoader(function() {
                return import("../productsClientsOffers/ListProductsClientsOffersPage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PRODUCTS_CLIENTS_OFFERS_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../productsClientsOffers/CreateProductsClientsOffersPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PRODUCTS_CLIENTS_OFFERS_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../productsClientsOffers/EditProductsClientsOffersPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PRODUCTS_SUPPLIERS_OFFERS,
            lazy: createPrivateLazyLoader(function() {
                return import("../productsSuppliersOffers/ListProductsSuppliersOffersPage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PRODUCTS_SUPPLIERS_OFFERS_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../productsSuppliersOffers/CreateProductsSuppliersOffersPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PRODUCTS_SUPPLIERS_OFFERS_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../productsSuppliersOffers/EditProductsSuppliersOffersPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PRODUCTS_CLIENTS_CODES,
            lazy: createPrivateLazyLoader(function() {
                return import("../productsClientsCodes/ListProductsClientsCodesPage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PRODUCTS_CLIENTS_CODES_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../productsClientsCodes/CreateProductsClientsCodesPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PRODUCTS_CLIENTS_CODES_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../productsClientsCodes/EditProductsClientsCodesPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PRODUCTS_SUPPLIERS_CODES,
            lazy: createPrivateLazyLoader(function() {
                return import("../productsSuppliersCodes/ListProductsSuppliersCodesPage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PRODUCTS_SUPPLIERS_CODES_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../productsSuppliersCodes/CreateProductsSuppliersCodesPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PRODUCTS_SUPPLIERS_CODES_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../productsSuppliersCodes/EditProductsSuppliersCodesPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PRODUCTS_VAT_RATES,
            lazy: createPrivateLazyLoader(function() {
                return import("../productsVatRates/ListProductVatRatesPage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PRODUCTS_VAT_RATES_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../productsVatRates/CreateProductVatRatePage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PRODUCTS_VAT_RATES_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../productsVatRates/EditProductVatRatePage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PRODUCTS_TRANSLATIONS,
            lazy: createPrivateLazyLoader(function() {
                return import("../productsTranslations/ListProductTranslationsPage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PRODUCTS_TRANSLATIONS_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../productsTranslations/CreateProductTranslationPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.PRODUCTS_TRANSLATIONS_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../productsTranslations/EditProductTranslationPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.ARTICLES_REPORTS,
            lazy: createPrivateLazyLoader(function() {
                return import("../app/UnderConstruction");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.CLIENTS_CONTRACTS,
            lazy: createPrivateLazyLoader(function() {
                return import("../app/UnderConstruction");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.CLIENTS_OFFERS,
            lazy: createPrivateLazyLoader(function() {
                return import("../app/UnderConstruction");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.CLIENTS_ORDERS,
            lazy: createPrivateLazyLoader(function() {
                return import("../app/UnderConstruction");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.CLIENTS_DESPATCHES,
            lazy: createPrivateLazyLoader(function() {
                return import("../app/UnderConstruction");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.CLIENTS_INVOICES,
            lazy: createPrivateLazyLoader(function() {
                return import("../invoices/clientInvoices/ListClientsInvoicesPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.CLIENTS_INVOICES_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../invoices/clientInvoices/CreateClientInvoicePage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.CLIENTS_INVOICES_CREATE_REFUND,
            lazy: createPrivateLazyLoader(function() {
                return import("../invoices/clientInvoices/RefundClientInvoicePage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.CLIENTS_INVOICES_DUPLICATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../invoices/clientInvoices/DuplicateClientInvoicePage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.CLIENTS_INVOICES_CREATE_FROM_CLIENT_GOODS_DESPATCH_ADVICE,
            lazy: createPrivateLazyLoader(function() {
                return import("../invoices/clientInvoices/CreateClientInvoicePage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.CLIENTS_INVOICES_CREATE_FROM_PROFORMA,
            lazy: createPrivateLazyLoader(function() {
                return import("../invoices/clientInvoices/CreateClientInvoiceFromProformaPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.CLIENTS_INVOICES_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../invoices/clientInvoices/EditClientInvoicePage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.CLIENTS_PROFORMAS_INVOICES,
            lazy: createPrivateLazyLoader(function() {
                return import("../proformaInvoices/ListProformaInvoicesPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.CLIENTS_PROFORMAS_INVOICES_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../proformaInvoices/CreateProformaInvoicePage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.CLIENTS_PROFORMAS_INVOICES_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../proformaInvoices/EditProformaInvoicePage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.CLIENTS_PROFORMAS_INVOICES_DUPLICATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../proformaInvoices/DuplicateProformaInvoicePage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.CLIENTS_PROFORMAS_INVOICES_VIEW,
            lazy: createPrivateLazyLoader(function() {
                return import("../proformaInvoices/ViewProformaInvoicePage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.CLIENTS_INVOICES_VIEW,
            lazy: createPrivateLazyLoader(function() {
                return import("../invoices/clientInvoices/ViewClientInvoicePage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.CLIENTS_INVOICES_EFACTURA_MESSAGES,
            lazy: createPrivateLazyLoader(function() {
                return import("../invoices/clientInvoices/ClientEfacturaMessagesPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.CLIENTS_PAYMENTS,
            lazy: createPrivateLazyLoader(function() {
                return import("../bankTransferPayments/clientsPayments/ListClientsPaymentsPage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.CLIENTS_PAYMENTS_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../bankTransferPayments/clientsPayments/CreateClientPaymentPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.CLIENTS_PAYMENTS_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../bankTransferPayments/clientsPayments/EditClientPaymentPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.CLIENTS_RECEIPTS,
            lazy: createPrivateLazyLoader(function() {
                return import("../receiptPayments/clientsReceipts/ListClientsReceiptsPage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.CLIENTS_RECEIPTS_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../receiptPayments/clientsReceipts/CreateClientReceiptPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.CLIENTS_RECEIPTS_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../receiptPayments/clientsReceipts/EditClientReceiptPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.CLIENTS_RECEIPTS_VIEW,
            lazy: createPrivateLazyLoader(function() {
                return import("../receiptPayments/clientsReceipts/ViewClientReceiptPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.CLIENTS_REPORTS,
            lazy: createPrivateLazyLoader(function() {
                return import("../reports/clientsReports/ListClientsReportsPage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            index: true,
            element: /*#__PURE__*/ React.createElement(Navigate, {
                to: routesParts.CLIENTS_STATEMENT_OF_ACCOUNT_REPORT
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.CLIENTS_STATEMENT_OF_ACCOUNT_REPORT,
            lazy: createPrivateLazyLoader(function() {
                return import("../reports/clientsReports/StatementOfAccountReportPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.CLIENTS_CURRENT_BALANCE_REPORT,
            lazy: createPrivateLazyLoader(function() {
                return import("../reports/clientsReports/CurrentBalanceReportPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.CLIENTS_OVERDUE_INVOICES_REPORT,
            lazy: createPrivateLazyLoader(function() {
                return import("../reports/clientsReports/OverdueInvoicesReportPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.CLIENTS_INVOICES_REPORT,
            lazy: createPrivateLazyLoader(function() {
                return import("../reports/clientsReports/InvoicesReportPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.CLIENTS_PAYMENTS_REPORT,
            lazy: createPrivateLazyLoader(function() {
                return import("../reports/clientsReports/PaymentsReportPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SUPPLIERS_CONTRACTS,
            lazy: createPrivateLazyLoader(function() {
                return import("../app/UnderConstruction");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SUPPLIERS_OFFERS,
            lazy: createPrivateLazyLoader(function() {
                return import("../app/UnderConstruction");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SUPPLIERS_ORDERS,
            lazy: createPrivateLazyLoader(function() {
                return import("../app/UnderConstruction");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SUPPLIERS_DESPATCHES,
            lazy: createPrivateLazyLoader(function() {
                return import("../app/UnderConstruction");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SUPPLIERS_INVOICES,
            lazy: createPrivateLazyLoader(function() {
                return import("../invoices/suppliersInvoices/ListSuppliersInvoicesPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SUPPLIERS_INVOICES_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../invoices/suppliersInvoices/CreateSupplierInvoicePage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SUPPLIERS_INVOICES_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../invoices/suppliersInvoices/EditSupplierInvoicePage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SUPPLIERS_INVOICES_VIEW,
            lazy: createPrivateLazyLoader(function() {
                return import("../invoices/suppliersInvoices/ViewSupplierInvoicePage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SUPPLIERS_INVOICES_EFACTURA_MESSAGES,
            lazy: createPrivateLazyLoader(function() {
                return import("../invoices/suppliersInvoices/SupplierEfacturaMessagesPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SUPPLIERS_INVOICES_CONFIRM_INVOICE,
            lazy: createPrivateLazyLoader(function() {
                return import("../invoices/suppliersInvoices/SupplierInvoiceConfirmPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SUPPLIERS_INVOICES_REJECT_INVOICE,
            lazy: createPrivateLazyLoader(function() {
                return import("../invoices/suppliersInvoices/SupplierInvoiceRejectPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SUPPLIERS_PAYMENTS,
            lazy: createPrivateLazyLoader(function() {
                return import("../bankTransferPayments/suppliersPayments/ListSuppliersPaymentsPage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SUPPLIERS_PAYMENTS_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../bankTransferPayments/suppliersPayments/CreateSupplierPaymentPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SUPPLIERS_PAYMENTS_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../bankTransferPayments/suppliersPayments/EditSupplierPaymentPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SUPPLIERS_RECEIPTS,
            lazy: createPrivateLazyLoader(function() {
                return import("../receiptPayments/suppliersReceipts/ListSuppliersReceiptsPage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SUPPLIERS_RECEIPTS_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../receiptPayments/suppliersReceipts/CreateSupplierReceiptPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SUPPLIERS_RECEIPTS_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../receiptPayments/suppliersReceipts/EditSupplierReceiptPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SUPPLIERS_REPORTS,
            lazy: createPrivateLazyLoader(function() {
                return import("../reports/suppliersReports/ListSuppliersReportsPage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            index: true,
            element: /*#__PURE__*/ React.createElement(Navigate, {
                to: routesParts.SUPPLIERS_STATEMENT_OF_ACCOUNT_REPORT
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SUPPLIERS_STATEMENT_OF_ACCOUNT_REPORT,
            lazy: createPrivateLazyLoader(function() {
                return import("../reports/suppliersReports/StatementOfAccountReportPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SUPPLIERS_CURRENT_BALANCE_REPORT,
            lazy: createPrivateLazyLoader(function() {
                return import("../reports/suppliersReports/CurrentBalanceReportPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SUPPLIERS_OVERDUE_INVOICES_REPORT,
            lazy: createPrivateLazyLoader(function() {
                return import("../reports/suppliersReports/OverdueInvoicesReportPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SUPPLIERS_INVOICES_REPORT,
            lazy: createPrivateLazyLoader(function() {
                return import("../reports/suppliersReports/InvoicesReportPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SUPPLIERS_PAYMENTS_REPORT,
            lazy: createPrivateLazyLoader(function() {
                return import("../reports/suppliersReports/PaymentsReportPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.STORE_MANAGEMENT_RECEIPT_NOTES,
            lazy: createPrivateLazyLoader(function() {
                return import("../app/UnderConstruction");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.STORE_MANAGEMENT_BONURI_CONSUM,
            lazy: createPrivateLazyLoader(function() {
                return import("../app/UnderConstruction");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.STORE_MANAGEMENT_BONURI_TRANSFER,
            lazy: createPrivateLazyLoader(function() {
                return import("../app/UnderConstruction");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.STORE_MANAGEMENT_BONURI_AUTORECEPTIE,
            lazy: createPrivateLazyLoader(function() {
                return import("../app/UnderConstruction");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.STORE_MANAGEMENT_INVENTORY,
            lazy: createPrivateLazyLoader(function() {
                return import("../app/UnderConstruction");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.STORE_MANAGEMENT_REPORTS,
            lazy: createPrivateLazyLoader(function() {
                return import("../app/UnderConstruction");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SETTINGS_BANKS,
            lazy: createPrivateLazyLoader(function() {
                return import("../banks/ListBanks");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SETTINGS_BANKS_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../banks/CreateBankPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SETTINGS_BANKS_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../banks/BankPage");
            }),
            id: "settings-banks"
        }, /*#__PURE__*/ React.createElement(Route, {
            index: true,
            lazy: createPrivateLazyLoader(function() {
                return import("../banks/EditBankPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SETTINGS_MEASUREMENT_UNITS,
            lazy: createPrivateLazyLoader(function() {
                return import("../measurementUnits/ListMeasurementUnitsPage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SETTINGS_MEASUREMENT_UNITS_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../measurementUnits/CreateMeasurementUnitPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SETTINGS_MEASUREMENT_UNITS_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../measurementUnits/MeasurementUnitPage");
            }),
            id: "settings-measurementUnits",
            shouldRevalidate: function(param) {
                var currentParams = param.currentParams, nextParams = param.nextParams;
                /**
                                             * We need to do this because shouldRevalidate will prevent
                                             * our revalidate call from EditMeasurementUnit and the
                                             * mu won't be synced in packaging pages
                                             */ var updatedAddress = currentParams.id && !nextParams.id;
                return nextParams.id !== currentParams.id || !!updatedAddress;
            }
        }, /*#__PURE__*/ React.createElement(Route, {
            index: true,
            lazy: createPrivateLazyLoader(function() {
                return import("../measurementUnits/EditMeasurementUnitPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SETTINGS_MEASUREMENT_UNITS_TRANSLATIONS,
            lazy: createPrivateLazyLoader(function() {
                return import("../measurementUnitsTranslations/ListMeasurementUnitTranslationsPage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SETTINGS_MEASUREMENT_UNITS_TRANSLATIONS_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../measurementUnitsTranslations/CreateMeasurementUnitTranslationPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SETTINGS_MEASUREMENT_UNITS_TRANSLATIONS_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../measurementUnitsTranslations/EditMeasurementUnitTranslationPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SETTINGS_VAT_RATES,
            lazy: createPrivateLazyLoader(function() {
                return import("../vatRates/ListVatRates");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SETTINGS_VAT_RATES_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../vatRates/CreateVatRatePage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SETTINGS_VAT_RATES_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../vatRates/VatRatePage");
            }),
            id: "settings-vatRates"
        }, /*#__PURE__*/ React.createElement(Route, {
            index: true,
            lazy: createPrivateLazyLoader(function() {
                return import("../vatRates/EditVatRatePage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SETTINGS_DOCUMENTS_SERIES,
            lazy: createPrivateLazyLoader(function() {
                return import("../documentsSeries/ListDocumentsSeriesPage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SETTINGS_DOCUMENTS_SERIES_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../documentsSeries/CreateDocumentsSeriesPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SETTINGS_DOCUMENTS_SERIES_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../documentsSeries/EditDocumentsSeriesPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.MANAGEMENT_USERS,
            lazy: createPrivateLazyLoader(function() {
                return import("../users/ListUsersPage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.MANAGEMENT_USERS_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../users/CreateUserPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.MANAGEMENT_USERS_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../users/UserPage");
            }),
            id: "sub-users"
        }, /*#__PURE__*/ React.createElement(Route, {
            index: true,
            lazy: createPrivateLazyLoader(function() {
                return import("../users/EditUserPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.ADMINISTRATION_RO_E_FACTURA_CODES,
            lazy: createPrivateLazyLoader(function() {
                return import("../app/UnderConstruction");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.ADMINISTRATION_COUNTRIES_CITIES,
            lazy: createPrivateLazyLoader(function() {
                return import("../app/UnderConstruction");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.ADMINISTRATION_CPV_CODES,
            lazy: createPrivateLazyLoader(function() {
                return import("../app/UnderConstruction");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.ADMINISTRATION_CURRENCIES,
            lazy: createPrivateLazyLoader(function() {
                return import("../app/UnderConstruction");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.ADMINISTRATION_MEASUREMENT_UNITS,
            lazy: createPrivateLazyLoader(function() {
                return import("../app/UnderConstruction");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.ADMINISTRATION_TAX_CODES,
            lazy: createPrivateLazyLoader(function() {
                return import("../app/UnderConstruction");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.ADMINISTRATION_USERS,
            lazy: createPrivateLazyLoader(function() {
                return import("../app/UnderConstruction");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.MY_ACCOUNT,
            lazy: createPrivateLazyLoader(function() {
                return import("../myAccount/MyAccountPage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.MY_ACCOUNT_CHANGE_DETAILS,
            lazy: createPrivateLazyLoader(function() {
                return import("../myAccount/ChangeAccountDetailsPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.MY_ACCOUNT_CHANGE_EMAIL,
            lazy: createPrivateLazyLoader(function() {
                return import("../myAccount/ChangeEmail/ChangeEmailPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.MY_ACCOUNT_CHANGE_PASSWORD,
            lazy: createPrivateLazyLoader(function() {
                return import("../myAccount/ChangePasswordPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.MY_ACCOUNT_START_2FA,
            lazy: createPrivateLazyLoader(function() {
                return import("../myAccount/TwoFa/TwoFaFlowActivate/TwoFaFlowActivatePage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.MY_ACCOUNT_REGENERATE_2FA,
            lazy: createPrivateLazyLoader(function() {
                return import("../myAccount/TwoFa/TwoFaFlowRegenerate/TwoFaFlowRegeneratePage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.MY_ACCOUNT_DEACTIVATE_2FA,
            lazy: createPrivateLazyLoader(function() {
                return import("../myAccount/TwoFa/TwoFaFlowDeactivate/TwoFaFlowDeactivatePage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.MY_ACCOUNT_EXPORTS,
            lazy: createPrivateLazyLoader(function() {
                return import("../myAccount/Exports/ExportsPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.MY_COMPANIES,
            lazy: createPrivateLazyLoader(function() {
                return import("../myCompanies/ListMyCompaniesPage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.MY_COMPANIES_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../myCompanies/CreateMyCompanyPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.MY_COMPANIES_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../myCompanies/MyCompanyPage");
            }),
            id: "my-company"
        }, /*#__PURE__*/ React.createElement(Route, {
            index: true,
            lazy: createPrivateLazyLoader(function() {
                return import("../myCompanies/EditMyCompanyPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.MY_COMPANIES_ADDRESSES,
            lazy: createPrivateLazyLoader(function() {
                return import("../myCompaniesAddresses/ListMyCompanyAddressesPage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.MY_COMPANIES_ADDRESSES_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../myCompaniesAddresses/CreateMyCompanyAddressPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.MY_COMPANIES_ADDRESSES_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../myCompaniesAddresses/EditMyCompanyAddressPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.MY_COMPANIES_CONTACTS,
            lazy: createPrivateLazyLoader(function() {
                return import("../myCompaniesContacts/ListMyCompanyContactsPage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.MY_COMPANIES_CONTACTS_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../myCompaniesContacts/CreateMyCompanyContactPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.MY_COMPANIES_CONTACTS_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../myCompaniesContacts/EditMyCompanyContactPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.MY_COMPANIES_BANK_ACCOUNTS,
            lazy: createPrivateLazyLoader(function() {
                return import("../myCompaniesBankAccounts/ListMyCompanyBankAccountsPage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.MY_COMPANIES_BANK_ACCOUNTS_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../myCompaniesBankAccounts/CreateMyCompanyBankAccountPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.MY_COMPANIES_BANK_ACCOUNTS_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../myCompaniesBankAccounts/EditMyCompanyBankAccountPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.MY_COMPANIES_SPV,
            lazy: createPrivateLazyLoader(function() {
                return import("../spv/AnafSpvPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.MY_COMPANIES_SUBSCRIPTIONS,
            lazy: createPrivateLazyLoader(function() {
                return import("../myCompaniesSubscriptions/MyCompanySubscriptionsPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SPV_EFACTURA,
            lazy: createPrivateLazyLoader(function() {
                return import("../eFactura/EFacturaListingPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SPV_ETRANSPORT,
            lazy: createPrivateLazyLoader(function() {
                return import("../eTransport/ETransportListingPage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SPV_ETRANSPORT_SUBSCRIPTION,
            lazy: createPrivateLazyLoader(function() {
                return import("../eTransport/Activate/EtransportSubscriptionPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SPV_ETRANSPORT_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../eTransport/EtransportDeclarationTypePage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SPV_ETRANSPORT_TRANSPORT_CONFIRMATION_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../eTransport/ConfirmTransport/CreateTransportConfirmationPage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SPV_ETRANSPORT_TRANSPORT_CONFIRMATION_CREATE_FOR_DECLARATION_ID
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SPV_ETRANSPORT_TRANSPORT_CONFIRMATION_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../eTransport/ConfirmTransport/EditTransportConfirmationPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SPV_ETRANSPORT_VEHICLE_MODIFICATION_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../eTransport/ModifyVehicle/CreateVehicleModificationPage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SPV_ETRANSPORT_VEHICLE_MODIFICATION_CREATE_FOR_DECLARATION_ID
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SPV_ETRANSPORT_VEHICLE_MODIFICATION_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../eTransport/ModifyVehicle/EditVehicleModificationPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SPV_ETRANSPORT_CREATE_CLIENT,
            lazy: createPrivateLazyLoader(function() {
                return import("../eTransport/CreateETransportDeclarationPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SPV_ETRANSPORT_CREATE_SUPPLIER,
            lazy: createPrivateLazyLoader(function() {
                return import("../eTransport/CreateETransportDeclarationPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SPV_ETRANSPORT_CREATE_GOODS_DESPATCH_ADVICE,
            lazy: createPrivateLazyLoader(function() {
                return import("../eTransport/CreateETransportDeclarationPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SPV_ETRANSPORT_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../eTransport/EditEtransportDeclarationPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SPV_ETRANSPORT_DUPLICATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../eTransport/DuplicateETransportDeclarationPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SPV_ETRANSPORT_ADD_CORRECTION,
            lazy: createPrivateLazyLoader(function() {
                return import("../eTransport/AddCorrectionForETransporDeclarationPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.SPV_ETRANSPORT_VIEW,
            lazy: createPrivateLazyLoader(function() {
                return import("../eTransport/ViewEtransportDeclarationPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.INVENTORY_WAREHOUSES,
            lazy: createPrivateLazyLoader(function() {
                return import("../warehouses/ListWarehousesPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.INVENTORY_WAREHOUSES_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../warehouses/CreateWarehousePage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.INVENTORY_WAREHOUSES_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../warehouses/EditWarehousePage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.INVENTORY_GOODS_RECEIPT_NOTES,
            lazy: createPrivateLazyLoader(function() {
                return import("../goodsReceiptNotes/ListGoodsReceiptNotesPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.INVENTORY_GOODS_RECEIPT_NOTES_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../goodsReceiptNotes/CreateGoodsReceiptNotePage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.INVENTORY_GOODS_RECEIPT_NOTES_CREATE_FROM_SUPPLIER_INVOICE,
            lazy: createPrivateLazyLoader(function() {
                return import("../goodsReceiptNotes/CreateGoodsReceiptNotePage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.INVENTORY_GOODS_RECEIPT_NOTES_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../goodsReceiptNotes/EditGoodsReceiptNotePage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.INVENTORY_GOODS_RECEIPT_NOTES_VIEW,
            lazy: createPrivateLazyLoader(function() {
                return import("../goodsReceiptNotes/ViewGoodsReceiptNotePage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.INVENTORY_GOODS_RECEIPT_NOTES_DUPLICATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../goodsReceiptNotes/DuplicateGoodsReceiptNotePage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.INVENTORY_GOODS_RECEIPT_NOTES_REFUND,
            lazy: createPrivateLazyLoader(function() {
                return import("../goodsReceiptNotes/RefundGoodsReceiptNotePage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.INVENTORY_STOCK_CONSUMPTION_RECEIPTS,
            lazy: createPrivateLazyLoader(function() {
                return import("../stockConsumptionReceipts/ListStockConsumptionReceiptsPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.INVENTORY_STOCK_CONSUMPTION_RECEIPT_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../stockConsumptionReceipts/CreateStockConsumptionReceiptPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.INVENTORY_STOCK_CONSUMPTION_RECEIPT_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../stockConsumptionReceipts/EditStockConsumptionReceiptPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.INVENTORY_STOCK_CONSUMPTION_RECEIPT_VIEW,
            lazy: createPrivateLazyLoader(function() {
                return import("../stockConsumptionReceipts/ViewStockConsumptionReceiptPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.INVENTORY_STOCK_CONSUMPTION_RECEIPT_DUPLICATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../stockConsumptionReceipts/DuplicateStockConsumptionReceiptPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.INVENTORY_STOCK_CONSUMPTION_RECEIPT_REFUND,
            lazy: createPrivateLazyLoader(function() {
                return import("../stockConsumptionReceipts/RefundStockConsumptionReceiptPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.INVENTORY_STOCK_RECEPTION_RECEIPTS,
            lazy: createPrivateLazyLoader(function() {
                return import("../stockReceptionReceipts/ListStockReceptionReceiptsPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.INVENTORY_STOCK_RECEPTION_RECEIPT_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../stockReceptionReceipts/CreateStockReceptionReceiptPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.INVENTORY_STOCK_RECEPTION_RECEIPT_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../stockReceptionReceipts/EditStockReceptionReceiptPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.INVENTORY_STOCK_RECEPTION_RECEIPT_VIEW,
            lazy: createPrivateLazyLoader(function() {
                return import("../stockReceptionReceipts/ViewStockReceptionReceiptPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.INVENTORY_STOCK_RECEPTION_RECEIPT_DUPLICATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../stockReceptionReceipts/DuplicateStockReceptionReceiptPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.INVENTORY_STOCK_RECEPTION_RECEIPT_REFUND,
            lazy: createPrivateLazyLoader(function() {
                return import("../stockReceptionReceipts/RefundStockReceptionReceiptPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.INVENTORY_CLIENT_GOODS_DESPATCH_ADVICES,
            lazy: createPrivateLazyLoader(function() {
                return import("../clientGoodsDespatchAdvices/ListClientGoodsDespatchAdvicesPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.INVENTORY_CLIENT_GOODS_DESPATCH_ADVICES_CREATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../clientGoodsDespatchAdvices/CreateClientGoodsDespatchAdvicesPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.INVENTORY_CLIENT_GOODS_DESPATCH_ADVICES_EDIT,
            lazy: createPrivateLazyLoader(function() {
                return import("../clientGoodsDespatchAdvices/EditClientGoodsDespatchAdvicesPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.INVENTORY_CLIENT_GOODS_DESPATCH_ADVICES_VIEW,
            lazy: createPrivateLazyLoader(function() {
                return import("../clientGoodsDespatchAdvices/ViewClientGoodsDespatchAdvicePage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.INVENTORY_CLIENT_GOODS_DESPATCH_ADVICES_DUPLICATE,
            lazy: createPrivateLazyLoader(function() {
                return import("../clientGoodsDespatchAdvices/DuplicateClientGoodsDespatchAdvicePage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.INVENTORY_CLIENT_GOODS_DESPATCH_ADVICES_REFUND,
            lazy: createPrivateLazyLoader(function() {
                return import("../clientGoodsDespatchAdvices/RefundClientGoodsDespatchAdvicePage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.INVENTORY_STOCKS_REPORTS,
            lazy: createPrivateLazyLoader(function() {
                return import("../reports/stocksReports/ListStocksReportsPage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            index: true,
            element: /*#__PURE__*/ React.createElement(Navigate, {
                to: routesParts.INVENTORY_STOCKS_REPORTS_CURRENT_STOCK
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.INVENTORY_STOCKS_REPORTS_CURRENT_STOCK,
            lazy: createPrivateLazyLoader(function() {
                return import("../reports/stocksReports/CurrentStockReportPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.INVENTORY_STOCKS_REPORTS_DATE_CURRENT_STOCK,
            lazy: createPrivateLazyLoader(function() {
                return import("../reports/stocksReports/CurrentStockReportPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.INVENTORY_STOCKS_REPORTS_STOCK_BALANCE,
            lazy: createPrivateLazyLoader(function() {
                return import("../reports/stocksReports/StockBalanceReportPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.INVENTORY_STOCKS_REPORTS_STOCKS_TRANSACTIONS,
            lazy: createPrivateLazyLoader(function() {
                return import("../reports/stocksReports/StocksTransactionsReportPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.INVENTORY_STOCKS_REPORTS_STOCK_RECORDS,
            lazy: createPrivateLazyLoader(function() {
                return import("../reports/stocksReports/StockRecordsReportPage");
            })
        }))), /*#__PURE__*/ React.createElement(Route, {
            element: /*#__PURE__*/ React.createElement(PublicLayout, null)
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.AUTH_LOGIN,
            lazy: createPublicLazyLoader(function() {
                return import("../auth/LoginPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.AUTH_VERIFY_EMAIL,
            lazy: createPrivateLazyLoader(function() {
                return import("../auth/EmailVerificationPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.CREATE_FIRST_COMPANY,
            lazy: createPrivateLazyLoader(function() {
                return import("../myCompanies/CreateMyFirstCompanyPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.AUTH_SIGNUP,
            lazy: createPublicLazyLoader(function() {
                return import("../auth/SignupPage");
            })
        }, /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.AUTH_SIGNUP_TERMS_OF_SERVICE,
            element: /*#__PURE__*/ React.createElement("div", {
                className: "fixed left-0 top-0 h-full w-full bg-black bg-opacity-90"
            }, /*#__PURE__*/ React.createElement("div", {
                className: "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-gray-50"
            }, "TERMS OF SERVICE"))
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.AUTH_FORGOT_PASSWORD,
            lazy: createPublicLazyLoader(function() {
                return import("./../auth/ForgotPasswordPages/StartForgotPasswordPage");
            })
        }), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.AUTH_FORGOT_PASSWORD_FINISH,
            lazy: createPublicLazyLoader(function() {
                return import("./../auth/ForgotPasswordPages/FinishForgotPasswordPage");
            })
        })), /*#__PURE__*/ React.createElement(Route, {
            path: routesParts.MY_COMPANIES_SPV_ACCOUNTANT,
            element: /*#__PURE__*/ React.createElement(AnafSpvAccountantFinishPage, null)
        }), /*#__PURE__*/ React.createElement(Route, {
            path: "*",
            element: /*#__PURE__*/ React.createElement(NotFoundErrorPage, null)
        })))))
    }));
}
